/**
 * Shared framework pattern matching utilities.
 *
 * Single source of truth for framework code -> substring patterns,
 * display ordering, and the matching function used across compliance UI.
 */

/** Substring patterns for classifying a reference string into a framework. */
export const FRAMEWORK_PATTERNS: Record<string, string[]> = {
  'SCUBA': ['SCUBA', 'MS.'],
  'NIST_800_53': ['NIST SP 800-53', 'NIST 800-53', 'NIST-800-53', 'NIST SP800-53'],
  'NIST_CSF': ['NIST CSF', 'CSF'],
  'MCSB': ['MCSB'],
  'EIDSCA': ['EIDSCA'],
  'MAESTER': ['MAESTER'],
  'CIS': ['CIS.'],
}

/** Returns true if `ref` contains any of the patterns for `frameworkCode`. */
export function refMatchesFramework(ref: string, frameworkCode: string): boolean {
  const patterns = FRAMEWORK_PATTERNS[frameworkCode] ?? [frameworkCode]
  const upperRef = ref.toUpperCase()
  return patterns.some(pattern => upperRef.includes(pattern.toUpperCase()))
}

/**
 * Pick the framework reference to display as the row's primary label.
 *
 * When a policy is cross-mapped to multiple frameworks (e.g.
 * `["MS.AAD.1.1v1", "MCSB.IM-3"]`), `frameworkReferences[0]` is whatever the
 * data pipeline happened to emit first — often a SCuBA control even when the
 * user has only starred MCSB. That makes unstarred frameworks look like
 * they're leaking into starred surfaces.
 *
 * Resolution: prefer a reference matching any starred framework code; fall
 * back to `[0]` so tenants with nothing starred (or with historical refs that
 * don't match any current framework) keep their existing label.
 */
export function pickPrimaryFrameworkRef(
  refs: string[] | undefined | null,
  starredCodes: string[],
): string | null {
  if (!refs || refs.length === 0) return null
  if (starredCodes.length > 0) {
    const starredMatch = refs.find(ref =>
      starredCodes.some(code => refMatchesFramework(ref, code)),
    )
    if (starredMatch) return starredMatch
  }
  return refs[0]
}

/** Preferred display ordering for frameworks. Lower = higher priority. */
export const FRAMEWORK_ORDER: Record<string, number> = {
  'MCSB': 0,
  'EIDSCA': 1,
  'NIST_800_53': 2,
  'SCUBA': 3,
  'CIS': 4,
}
